import React, { useEffect, useState, useMemo } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../ui/tooltip';
import { toast } from 'sonner';
import { FileArrowDown, PaperPlaneTilt } from '@phosphor-icons/react';
import {
  generate3plInvoices, list3plInvoices, markPaid3plInvoice, void3plInvoice,
  invoice3plPdfUrl, send3plInvoice, send3plAllDrafts,
} from '../../lib/api';
import { fmt$, fmtDate } from './utils';

export default function InvoicesTab({ merchants, onRefresh }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [genOpen, setGenOpen] = useState(false);
  const [genForm, setGenForm] = useState(() => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    return { period_start: start.toISOString().slice(0, 10), period_end: end.toISOString().slice(0, 10) };
  });
  const [gen, setGen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendInv, setSendInv] = useState(null);
  const [sendForm, setSendForm] = useState({ to: '', note: '' });
  const [sending, setSending] = useState(false);
  // Sort state for table headers (client-side; data already loaded)
  const [sort, setSort] = useState({ key: 'created_at', dir: 'desc' });
  // Bulk-send dialog state
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkNote, setBulkNote] = useState('');

  const draftCount = invoices.filter((inv) => inv.status === 'draft').length;

  // Header click handler — toggles direction when re-clicking the active key,
  // otherwise sets a sensible default direction per column type.
  const headerSort = (key, defaultDir = 'desc') => {
    setSort((cur) => cur.key === key
      ? { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: defaultDir });
  };

  // Comparator that knows which fields are dates / numbers / strings. We
  // sort an array copy so the source `invoices` state stays stable for
  // unrelated callers (delivery dot lookups, etc).
  const sortedInvoices = useMemo(() => {
    const list = [...invoices];
    const { key, dir } = sort;
    const sign = dir === 'asc' ? 1 : -1;
    const numKeys = new Set(['total']);
    const dateKeys = new Set(['period_start', 'due_date', 'created_at']);
    list.sort((a, b) => {
      let av = a[key]; let bv = b[key];
      if (key === 'merchant') {
        av = a.merchant_name || (merchants.find((m) => m.id === a.merchant_id)?.name) || '';
        bv = b.merchant_name || (merchants.find((m) => m.id === b.merchant_id)?.name) || '';
      }
      if (numKeys.has(key)) {
        return ((Number(av) || 0) - (Number(bv) || 0)) * sign;
      }
      if (dateKeys.has(key)) {
        return ((new Date(av || 0)).getTime() - (new Date(bv || 0)).getTime()) * sign;
      }
      av = (av ?? '').toString().toLowerCase();
      bv = (bv ?? '').toString().toLowerCase();
      return av < bv ? -sign : av > bv ? sign : 0;
    });
    return list;
  }, [invoices, sort, merchants]);

  // Tiny chevron next to the active column header so operators see the
  // current sort key without extra UI noise.
  const SortHead = ({ k, label, defaultDir = 'desc', align = 'left' }) => (
    <th className={`p-2 ${align === 'right' ? 'text-right' : 'text-left'}`}>
      <button
        type="button"
        onClick={() => headerSort(k, defaultDir)}
        className="inline-flex items-center gap-1 font-bold uppercase tracking-wider text-[11px] hover:text-foreground"
        data-testid={`invoice-sort-${k}`}
      >
        {label}
        {sort.key === k && (
          <span className="text-[9px]">{sort.dir === 'asc' ? '▲' : '▼'}</span>
        )}
      </button>
    </th>
  );

  // Maps the email_logs.status (also mirrored onto invoice.last_sent_status)
  // to a small colored circle + tooltip describing what's happened. Shown
  // only when the invoice has actually been sent (last_sent_at set).
  const DeliveryDot = ({ inv }) => {
    if (!inv.last_sent_at) {
      return <span className="text-muted-foreground text-xs">—</span>;
    }
    const s = inv.last_sent_status || 'unknown';
    const config = {
      delivered:  { color: 'bg-emerald-500', label: 'Delivered',  pulse: false },
      sent:       { color: 'bg-sky-500',     label: 'Sent (awaiting delivery)', pulse: true },
      bounced:    { color: 'bg-rose-500',    label: 'Bounced',    pulse: false },
      complained: { color: 'bg-orange-500',  label: 'Complained', pulse: false },
      mocked:     { color: 'bg-slate-400',   label: 'Mocked (RESEND_API_KEY unset)', pulse: false },
      error:      { color: 'bg-rose-700',    label: 'Send error', pulse: false },
    }[s] || { color: 'bg-slate-300', label: `Unknown (${s})`, pulse: false };

    const events = inv.last_sent_events || [];
    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button type="button" className="inline-flex items-center gap-1.5 cursor-help"
              data-testid={`invoice-delivery-dot-${inv.id}`}>
              <span className={`relative inline-block w-2.5 h-2.5 rounded-full ${config.color}`}>
                {config.pulse && (
                  <span className={`absolute inset-0 rounded-full ${config.color} opacity-60 animate-ping`} />
                )}
              </span>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground hidden md:inline">
                {s}
              </span>
            </button>
          </TooltipTrigger>
          <TooltipContent side="left" className="max-w-xs">
            <div className="space-y-1.5">
              <div className="font-semibold text-sm">{config.label}</div>
              {inv.last_sent_to && (
                <div className="text-[11px] text-muted-foreground">
                  to <span className="font-mono">{inv.last_sent_to}</span>
                </div>
              )}
              {inv.last_sent_at && (
                <div className="text-[11px] text-muted-foreground">
                  Sent {fmtDate(inv.last_sent_at)}
                </div>
              )}
              {events.length > 0 && (
                <div className="border-t pt-1.5 mt-1.5">
                  <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70 mb-1">
                    Last events
                  </div>
                  <ul className="space-y-0.5">
                    {events.map((e, idx) => (
                      <li key={idx} className="text-[11px] flex items-center gap-2">
                        <span className="font-mono text-muted-foreground">{fmtDate(e.received_at)}</span>
                        <span className="font-semibold">{(e.type || '').replace('email.', '')}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {events.length === 0 && s !== 'mocked' && (
                <div className="text-[11px] text-muted-foreground italic">
                  No webhook events received yet.
                </div>
              )}
              {s === 'mocked' && (
                <div className="text-[11px] text-muted-foreground italic">
                  Configure RESEND_API_KEY for real delivery + webhook events.
                </div>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const r = await list3plInvoices();
      setInvoices(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load invoices');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchInvoices(); }, []);

  const generate = async () => {
    setGen(true);
    try {
      const body = {
        period_start: new Date(genForm.period_start).toISOString(),
        period_end: new Date(genForm.period_end).toISOString(),
      };
      const r = await generate3plInvoices(body);
      toast.success(`${r.data.created_count} invoice(s) generated`);
      setGenOpen(false);
      fetchInvoices();
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Generate failed');
    } finally {
      setGen(false);
    }
  };
  const markPaid = async (inv) => {
    if (!window.confirm(`Mark invoice ${inv.invoice_number} (${fmt$(inv.total)}) as paid?`)) return;
    try {
      await markPaid3plInvoice(inv.id);
      toast.success('Marked paid');
      fetchInvoices();
      onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const voidInv = async (inv) => {
    if (!window.confirm(`Void ${inv.invoice_number}? Charges will be re-opened.`)) return;
    try {
      await void3plInvoice(inv.id);
      toast.success('Invoice voided');
      fetchInvoices();
      onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const beginSend = (inv) => {
    const merchant = merchants.find((m) => m.id === inv.merchant_id);
    setSendInv(inv);
    setSendForm({
      to: merchant?.billing_email || merchant?.contact_email || '',
      note: '',
    });
    setSendOpen(true);
  };
  const submitSend = async () => {
    if (!sendInv) return;
    setSending(true);
    try {
      const body = {};
      if (sendForm.to.trim()) body.to = sendForm.to.trim();
      if (sendForm.note.trim()) body.note = sendForm.note.trim();
      const r = await send3plInvoice(sendInv.id, body);
      const status = r.data?.status;
      const mocked = r.data?.mocked;
      if (mocked) {
        toast.success(`Email queued (MOCK) — set RESEND_API_KEY to send for real. Recipient: ${r.data?.to}`);
      } else if (status === 'sent') {
        toast.success(`Invoice sent to ${r.data?.to}`);
      } else {
        toast.warning(`Send finished with status: ${status}`);
      }
      setSendOpen(false);
      fetchInvoices();
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const beginBulk = () => {
    setBulkResult(null);
    setBulkNote('');
    setBulkOpen(true);
  };
  const submitBulk = async () => {
    setBulkRunning(true);
    try {
      const body = {};
      if (bulkNote.trim()) body.note = bulkNote.trim();
      const r = await send3plAllDrafts(body);
      setBulkResult(r.data);
      const { processed, sent, failed } = r.data || {};
      if (processed === 0) {
        toast.info('No draft invoices to send.');
      } else if (failed === 0) {
        toast.success(`Sent ${sent}/${processed} draft invoice(s).`);
      } else {
        toast.warning(`Sent ${sent}/${processed} draft invoice(s), ${failed} failed — see breakdown below.`);
      }
      fetchInvoices();
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk send failed');
    } finally {
      setBulkRunning(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold">Invoices</h2>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={beginBulk}
            disabled={draftCount === 0}
            data-testid="invoice-send-all-drafts-btn"
            title={draftCount === 0 ? 'No draft invoices to send' : `Send ${draftCount} draft invoice(s) at once`}
          >
            <PaperPlaneTilt size={14} className="mr-1" />
            Send all drafts {draftCount > 0 && <span className="ml-1 text-xs font-mono">({draftCount})</span>}
          </Button>
          <Button onClick={() => setGenOpen(true)} data-testid="invoice-generate-btn">
            <FileArrowDown size={14} className="mr-1" /> Generate invoices
          </Button>
        </div>
      </div>
      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 border-b border-border">
            <tr className="text-left">
              <SortHead k="invoice_number" label="Number" defaultDir="asc" />
              <SortHead k="merchant" label="Merchant" defaultDir="asc" />
              <SortHead k="period_start" label="Period" />
              <SortHead k="due_date" label="Due" />
              <SortHead k="total" label="Total" align="right" />
              <SortHead k="status" label="Status" defaultDir="asc" />
              <th className="p-2"><span className="font-bold uppercase tracking-wider text-[11px]">Delivery</span></th>
              <th className="p-2 w-44 text-right"><span className="font-bold uppercase tracking-wider text-[11px]">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && invoices.length === 0 && (
              <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">
                No invoices yet. Use "Generate invoices" to bill open charges for a period.
              </td></tr>
            )}
            {sortedInvoices.map((inv) => {
              const merchant = merchants.find((m) => m.id === inv.merchant_id);
              const isFinal = inv.status === 'paid' || inv.status === 'void' || inv.status === 'voided';
              return (
                <tr key={inv.id} className="border-b border-border last:border-b-0" data-testid={`invoice-row-${inv.id}`}>
                  <td className="p-2 font-mono font-bold">{inv.invoice_number}</td>
                  <td className="p-2">{inv.merchant_name || merchant?.name || '—'}</td>
                  <td className="p-2 text-xs">{fmtDate(inv.period_start)} – {fmtDate(inv.period_end)}</td>
                  <td className="p-2 text-xs">{fmtDate(inv.due_date)}</td>
                  <td className="p-2 text-right font-mono font-bold">{fmt$(inv.total)}</td>
                  <td className="p-2">
                    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 ${
                      inv.status === 'paid' ? 'bg-emerald-100 text-emerald-800' :
                      inv.status === 'sent' ? 'bg-sky-100 text-sky-800' :
                      (inv.status === 'voided' || inv.status === 'void') ? 'bg-slate-200 text-slate-600' :
                      'bg-amber-100 text-amber-800'
                    }`}>
                      {inv.status}
                    </span>
                  </td>
                  <td className="p-2">
                    <DeliveryDot inv={inv} />
                  </td>
                  <td className="p-2 text-right space-x-1">
                    <a href={invoice3plPdfUrl(inv.id)} target="_blank" rel="noreferrer"
                      className="inline-block text-xs px-2 py-1 border border-border hover:bg-muted"
                      data-testid={`invoice-pdf-${inv.id}`}>PDF</a>
                    {inv.last_pay_url && !isFinal && (
                      <a href={inv.last_pay_url} target="_blank" rel="noreferrer"
                        className="inline-block text-xs px-2 py-1 border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 font-semibold"
                        data-testid={`invoice-pay-link-${inv.id}`}>
                        Pay link
                      </a>
                    )}
                    {!isFinal && (
                      <Button size="sm" variant="outline" onClick={() => beginSend(inv)} data-testid={`invoice-send-${inv.id}`}>
                        Send
                      </Button>
                    )}
                    {!isFinal && (
                      <Button size="sm" variant="outline" onClick={() => markPaid(inv)} data-testid={`invoice-mark-paid-${inv.id}`}>
                        Paid
                      </Button>
                    )}
                    {!isFinal && (
                      <Button size="sm" variant="outline" className="text-rose-700" onClick={() => voidInv(inv)} data-testid={`invoice-void-${inv.id}`}>
                        Void
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="max-w-lg" data-testid="invoice-generate-dialog">
          <DialogHeader>
            <DialogTitle>Generate invoices</DialogTitle>
            <DialogDescription>
              Pulls all open charges in this window per merchant, applies minimum-monthly floors, creates draft invoices.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Period start</Label>
                <Input type="date" value={genForm.period_start}
                  onChange={(e) => setGenForm({ ...genForm, period_start: e.target.value })}
                  data-testid="invoice-gen-period-start" />
              </div>
              <div>
                <Label>Period end</Label>
                <Input type="date" value={genForm.period_end}
                  onChange={(e) => setGenForm({ ...genForm, period_end: e.target.value })}
                  data-testid="invoice-gen-period-end" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenOpen(false)}>Cancel</Button>
            <Button onClick={generate} disabled={gen} data-testid="invoice-gen-submit">
              {gen ? 'Generating…' : 'Generate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-lg" data-testid="invoice-send-dialog">
          <DialogHeader>
            <DialogTitle>Send invoice {sendInv?.invoice_number}</DialogTitle>
            <DialogDescription>
              Emails the rendered PDF to your merchant. Set <code>RESEND_API_KEY</code> in
              backend env to enable real delivery — without it, the email is logged
              to the audit trail but not actually sent.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>To</Label>
              <Input type="email" value={sendForm.to}
                onChange={(e) => setSendForm({ ...sendForm, to: e.target.value })}
                placeholder="merchant billing email"
                data-testid="invoice-send-to" />
              <div className="text-[11px] text-muted-foreground mt-1">
                Defaults to the merchant's billing email; falls back to the contact email.
              </div>
            </div>
            <div>
              <Label>Note (optional)</Label>
              <textarea value={sendForm.note}
                onChange={(e) => setSendForm({ ...sendForm, note: e.target.value })}
                rows={3}
                className="w-full border border-border bg-background p-2 text-sm"
                placeholder="Short cover note that appears above the attachment in the email body."
                data-testid="invoice-send-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)}>Cancel</Button>
            <Button onClick={submitSend} disabled={sending || !sendForm.to.trim()} data-testid="invoice-send-submit">
              {sending ? 'Sending…' : 'Send invoice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-2xl" data-testid="invoice-bulk-send-dialog">
          <DialogHeader>
            <DialogTitle>Send all draft invoices</DialogTitle>
            <DialogDescription>
              Each draft invoice goes to its own merchant's billing email
              (falling back to contact email). Invoices without any recipient
              fail individually — the rest still send. Voided invoices are skipped.
            </DialogDescription>
          </DialogHeader>
          {!bulkResult && (
            <div className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 p-3 text-sm">
                <span className="font-bold">{draftCount}</span> draft invoice{draftCount === 1 ? '' : 's'} will be processed.
                Each merchant receives only their own invoice.
              </div>
              <div>
                <Label>Cover note (applied to every email, optional)</Label>
                <textarea value={bulkNote}
                  onChange={(e) => setBulkNote(e.target.value)}
                  rows={3}
                  className="w-full border border-border bg-background p-2 text-sm"
                  placeholder="e.g. 'April invoices attached — please remit by the 15th. Reach out with any questions.'"
                  data-testid="invoice-bulk-note" />
              </div>
            </div>
          )}
          {bulkResult && (
            <div className="space-y-2">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="border border-border p-3">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Processed</div>
                  <div className="text-2xl font-bold font-mono">{bulkResult.processed}</div>
                </div>
                <div className="border border-emerald-200 bg-emerald-50 p-3">
                  <div className="text-xs uppercase tracking-wider text-emerald-700">Sent</div>
                  <div className="text-2xl font-bold font-mono text-emerald-800">{bulkResult.sent}</div>
                </div>
                <div className={`border ${bulkResult.failed ? 'border-rose-200 bg-rose-50' : 'border-border'} p-3`}>
                  <div className={`text-xs uppercase tracking-wider ${bulkResult.failed ? 'text-rose-700' : 'text-muted-foreground'}`}>Failed</div>
                  <div className={`text-2xl font-bold font-mono ${bulkResult.failed ? 'text-rose-800' : ''}`}>{bulkResult.failed}</div>
                </div>
              </div>
              {(bulkResult.results || []).length > 0 && (
                <div className="max-h-72 overflow-y-auto border border-border bg-card">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40 border-b border-border sticky top-0">
                      <tr className="text-left">
                        <th className="p-2">Invoice</th>
                        <th className="p-2">Result</th>
                        <th className="p-2">Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkResult.results.map((r, idx) => (
                        <tr key={idx} className="border-b border-border last:border-b-0" data-testid={`bulk-result-row-${idx}`}>
                          <td className="p-2 font-mono">{r.invoice_number || r.invoice_id}</td>
                          <td className="p-2">
                            <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 ${r.sent ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>
                              {r.sent ? (r.mocked ? 'sent (mock)' : 'sent') : 'failed'}
                            </span>
                          </td>
                          <td className="p-2 text-muted-foreground">{r.error || r.to || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)} data-testid="invoice-bulk-close-btn">
              {bulkResult ? 'Close' : 'Cancel'}
            </Button>
            {!bulkResult && (
              <Button onClick={submitBulk} disabled={bulkRunning || draftCount === 0} data-testid="invoice-bulk-submit">
                {bulkRunning ? 'Sending…' : `Send ${draftCount} invoice${draftCount === 1 ? '' : 's'}`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
